import { useEffect, useState } from 'react';
import { useI18n } from '@/i18n';
import { MODEL_ROUTING } from '@/config/models.config';
import { AI_PROVIDERS } from '@/config/aiProviders.config';
import { DATA_MODE } from '@/config/dataMode';
import { fetchBackendModels, type BackendModelEntry } from '@/services/api/modelsApi';

export function AIModelsPage() {
  const { t } = useI18n();
  const [liveModels, setLiveModels] = useState<BackendModelEntry[] | null>(null);

  useEffect(() => {
    if (DATA_MODE !== 'live') return;

    let active = true;

    fetchBackendModels()
      .then((models) => {
        if (active) setLiveModels(models);
      })
      .catch(() => {
        if (active) setLiveModels(null);
      });

    return () => {
      active = false;
    };
  }, []);

  const useLive = DATA_MODE === 'live' && liveModels !== null;

  return (
    <div>
      <h1>{t('nav.aiModels')}</h1>
      <p style={{ color: 'var(--text-2)', marginTop: -8, marginBottom: 16 }}>
        {t('aiModels.subtitle')}
      </p>

      <div className="card" style={{ padding: 8, marginBottom: 16 }}>
        <div className="table-scroll">
          <table className="dt">
            <thead>
              <tr>
                <th>{t('aiModels.provider')}</th>
                <th>{t('aiModels.model')}</th>
                <th>{t('aiModels.purposeCol')}</th>
                <th>{t('aiModels.status')}</th>
              </tr>
            </thead>

            <tbody>
              {useLive
                ? liveModels.map((m) => {
                    const connected = m.status === 'available';

                    return (
                      <tr key={`${m.provider}-${m.modelId}`}>
                        <td>{AI_PROVIDERS[m.provider].displayName}</td>
                        <td>{m.displayName}</td>
                        <td>{m.purpose ?? '—'}</td>
                        <td>
                          <span className={`badge ${connected ? 'good' : 'neutral'}`}>
                            {connected ? t('aiModels.configured') : t('common.notConnected')}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                : MODEL_ROUTING.map((m, i) => (
                    <tr key={`${m.provider}-${m.model}-${i}`}>
                      <td>{AI_PROVIDERS[m.provider].displayName}</td>
                      <td>{m.model}</td>
                      <td>{t(m.purposeKey)}</td>
                      <td>
                        <span className={`badge ${m.status === 'configured' ? 'good' : 'neutral'}`}>
                          {m.status === 'configured'
                            ? t('aiModels.configured')
                            : t('common.notConnected')}
                        </span>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <p style={{ fontSize: 13, color: 'var(--text-2)', margin: 0 }}>
          {t('aiModels.note')}
        </p>
      </div>
    </div>
  );
}
